/**
 * Ogg Opus encoding for talk-time audio segments.
 *
 * Why: every segment is uploaded to S3 and then base64'd to Gemini by the
 * transcription Lambda. That upload is essentially all of the account's Lambda
 * egress. Opus at 32 kbps carries the same speech as our 16 kHz PCM WAV at
 * roughly a seventh of the bytes, so it cuts both this app's upload and the
 * Lambda's egress.
 *
 * Why not MediaRecorder: the audio arrives as a Float32Array from an
 * AudioWorklet after VAD trimming, not as a MediaStream, and routing it back
 * through an AudioContext to obtain one would force the encode to run in real
 * time (a 10 s chunk would take 10 s). mediabunny encodes from raw samples via
 * WebCodecs and muxes to Ogg, both faster than real time.
 *
 * Why mediabunny rather than hand-rolling the container: Ogg framing has several
 * things that are quiet to get wrong — the lacing table, the CRC-32, and above
 * all the closing granule position, which must be clamped to the true sample
 * count or the file decodes up to one frame long. This is not a format worth
 * owning to save ~22 KB of bundle.
 */
import {
  Output,
  OggOutputFormat,
  BufferTarget,
  AudioSampleSource,
  AudioSample,
  canEncodeAudio,
} from 'mediabunny';

/**
 * 32k was validated against prod audio (5 codecs x 100 real turns x 5 repeats):
 * no content loss versus WAV. `bitrate` is deprecated in favour of `quality`,
 * but the Quality presets cannot express an exact rate and we want the value
 * that was actually measured, not an approximation of it.
 */
const OPUS_BITRATE = 32_000;
const OPUS_CHANNELS = 1;

/**
 * Cached capability probe, keyed by sample rate — support is rate-dependent, so
 * probing one rate says nothing about another. `canEncodeAudio` is async and
 * hits the WebCodecs registry, so each rate is resolved once and reused: this
 * runs per audio segment, several times a minute per participant.
 */
const opusSupport = new Map<number, Promise<boolean>>();

export const isOpusEncodingSupported = (sampleRate: number): Promise<boolean> => {
  let probe = opusSupport.get(sampleRate);
  if (!probe) {
    probe = canEncodeAudio('opus', {
      numberOfChannels: OPUS_CHANNELS,
      sampleRate,
      bitrate: OPUS_BITRATE,
    }).catch(() => false);
    opusSupport.set(sampleRate, probe);
  }
  return probe;
};

/**
 * Encodes mono Float32 PCM as Ogg Opus.
 *
 * Resolves null on any failure — unsupported runtime, encoder error, or empty
 * output — so the caller can fall back to WAV. This sits on the live class path
 * and must never be the reason a turn fails to transcribe.
 */
export const encodeOpus = async (
  samples: Float32Array,
  sampleRate: number,
): Promise<Uint8Array | null> => {
  if (samples.length === 0) return null;
  if (!(await isOpusEncodingSupported(sampleRate))) return null;

  let output: Output | null = null;
  let finalized = false;

  try {
    output = new Output({
      format: new OggOutputFormat(),
      target: new BufferTarget(),
    });

    // sampleRate and numberOfChannels are deliberately absent here — they are
    // not part of AudioEncodingConfig. mediabunny derives them from the
    // AudioSample passed to add(), and configures WebCodecs from that.
    const source = new AudioSampleSource({
      codec: 'opus',
      bitrate: OPUS_BITRATE,
    });
    output.addAudioTrack(source);

    await output.start();

    // timestamp is in SECONDS here, unlike the WebCodecs AudioData it wraps,
    // which uses microseconds.
    const sample = new AudioSample({
      data: samples,
      format: 'f32',
      numberOfChannels: OPUS_CHANNELS,
      sampleRate,
      timestamp: 0,
    });

    try {
      // Awaited to respect encoder and writer backpressure.
      await source.add(sample);
    } finally {
      sample.close();
    }

    await output.finalize();
    finalized = true;

    const buffer = (output.target as BufferTarget).buffer;
    if (!buffer || buffer.byteLength === 0) return null;
    return new Uint8Array(buffer);
  } catch {
    return null;
  } finally {
    // If we threw between start() and finalize(), the underlying WebCodecs
    // encoder is still open. cancel() closes it. Only finalize() or cancel()
    // release it, and this recorder runs for the length of a class.
    if (output && !finalized) {
      try {
        await output.cancel();
      } catch {
        /* nothing further we can do */
      }
    }
  }
};
